

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslations } from "next-intl";
import { motion, AnimatePresence } from "framer-motion";
import {
  IconSparkles,
  IconChevronDown,
  IconCircleCheckFilled,
  IconArrowRight,
} from "@tabler/icons-react";
import { useOnboarding } from "@/providers/OnboardingProvider";
import { useBackgroundJobs } from "@/providers/BackgroundJobsProvider";
import { useCoachStore } from "@/lib/onboarding/coach-store";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/components/Toast/Toast";
import { startPointer } from "@/lib/onboarding/pointer-store";
import { consumeSetupAutoOpen } from "@/lib/onboarding/setup-autoopen";
import { ActiveStep } from "@/components/POnboarding/onboarding-steps";
import type { Event as VeltricEvent } from "@/types/Event";
import { Plugin } from "@/types/enums/Plugin";
import { useCapabilitiesOptional } from "@/providers/CapabilitiesProvider";
import { useSetupPlan } from "@/lib/query/hooks/useSetupPlan";
import { useVisibleSetupSections } from "@/pages/event/setup/useVisibleSetupSections";
import { eventLayoutEventKey } from "@/lib/query/hooks/useEventLayoutEvent";
import { OnboardingStepRow } from "./OnboardingStepRow";
import { OnboardingSetupTrack } from "./OnboardingSetupTrack";
import { isImmersiveChromeRoute } from "@/lib/immersiveRoutes";
import { isProgrammeRoute } from "@/components/POnboarding/OnboardingCoach";
import { useProgrammePanelStore } from "@/lib/onboarding/programme-panel-store";

export const POnboarding: React.FC = () => {
  const {
    activeSteps,
    completedCount,
    totalCount,
    percentComplete,
    isMinimized,
    isDismissed,
    justCompletedAll,
    recentlyCompletedKeys,
    toggleMinimized,
  } = useOnboarding();

  const { jobs } = useBackgroundJobs();
  // The persistent coach owns the first-run journey; the checklist pill defers
  // to it (same bottom-right slot) while it's active.
  const journeyActive = useCoachStore((s) => s.journeyActive);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  // Hide while a blocking full-screen / docked overlay is open (offering wizard,
  // lane popover, appointment modal, …) so this bottom-right pill can't cover
  // the overlay's action buttons — same signal the coach uses.
  const overlayOpen = useCoachStore((s) => s.overlayCount > 0);
  // Hide while the guest list's CRM transfer mode is active: its contact pane
  // docks exactly under this fixed bottom-right panel, which would cover the
  // pane's rows and swallow its drops (narrow signal — see coach-store).
  const crmSuppressed = useCoachStore((s) => s.crmModeCount > 0);
  const t = useTranslations("onboarding");
  const tAi = useTranslations("aiAssistant");
  const pathname = useLocation().pathname;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  const isAuthRoute = pathname?.startsWith("/auth");
  const isImmersiveRoute = isImmersiveChromeRoute(pathname);

  // Programme routes: the timeline grid owns the bottom-right canvas corner
  // this panel floats over, so the panel DEFAULT-minimizes there (same route
  // gate the coach uses). An explicit expand via the appbar pill wins for the
  // session (shared store — the pill is a separate component); the panel's
  // own chevron clears it again.
  const programmeExpanded = useProgrammePanelStore((s) => s.userExpanded);
  const setProgrammeExpanded = useProgrammePanelStore(
    (s) => s.setUserExpanded,
  );
  const routeMinimized = isProgrammeRoute(pathname) && !programmeExpanded;


  const eventId = useMemo(() => {
    // ANCHORED: `/user/subscription/event/:id` lives under the DashboardShell
    // and would otherwise resolve an eventId with no event scope around it.
    const match = pathname?.match(/^\/event\/([^/]+)/);
    return match?.[1] ?? null;
  }, [pathname]);

  // Event-scoped setup journey folded into this panel as its own group. The
  // plan read is cap-gated (useCapabilitiesOptional is null-safe on the
  // company dashboard where no CapabilitiesProvider exists) and only fires
  // inside an event. `setupPending` keeps the panel visible independently of
  // the company onboarding's dismissed/complete state.
  const caps = useCapabilitiesOptional();
  const canReadSetup = caps.can("event-setup:read");
  const setupPlanQuery = useSetupPlan(eventId && canReadSetup ? eventId : null);
  const setupPlan = setupPlanQuery.data ?? null;
  // Open sections, minus the ones whose sidebar module this event doesn't have
  // — the same list the track renders, so the group never opens empty.
  const visibleSetupSections = useVisibleSetupSections(eventId, setupPlan);
  const hasSetup = !!eventId && canReadSetup && visibleSetupSections.length > 0;

  // One-shot intro banner + auto-expand on a freshly created event: the create
  // flow flags the event; we consume the flag once and remember which event it
  // was for. Both are EVENT-LOCAL — we must NOT persist the COMPANY
  // onboarding's dismissed/minimized state (localStorage + backend) here: that
  // would resurrect a checklist the user deliberately dismissed.
  // `forceSetupOpenId` only overrides the programme route's DEFAULT minimize
  // for this render — never an explicit one (see shouldShowPanel below).
  const [introEventId, setIntroEventId] = useState<string | null>(null);
  const [forceSetupOpenId, setForceSetupOpenId] = useState<string | null>(null);
  // The company "default" onboarding collapses under the event setup group so
  // the panel stays focused on this event; expandable on demand. Without a
  // setup group it renders inline as before.
  const [companyOpen, setCompanyOpen] = useState(false);
  const autoOpenedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!eventId || !hasSetup) return;
    if (autoOpenedRef.current === eventId) return;
    const flag = consumeSetupAutoOpen(eventId);
    if (flag) {
      autoOpenedRef.current = eventId;
      // "quiet": the handoff lost the interview briefs — open the group, but
      // the intro would claim setup work the event never received.
      if (flag !== "quiet") setIntroEventId(eventId);
      setForceSetupOpenId(eventId);
    }
  }, [eventId, hasSetup]);
  // Leaving the event drops its one-shot nudges so returning later in the same
  // session (the panel instance persists across event switches — no key on
  // EventLayoutShell) doesn't replay the intro or re-expand a parked panel.
  useEffect(() => {
    setIntroEventId((prev) => (prev && prev !== eventId ? null : prev));
    setForceSetupOpenId((prev) => (prev && prev !== eventId ? null : prev));
  }, [eventId]);
  const showSetupIntro = hasSetup && introEventId === eventId;
  const forceSetupOpen = hasSetup && forceSetupOpenId === eventId;

  // Check if background jobs popup is visible (has any jobs for current event)
  const hasVisibleBackgroundJobs = useMemo(() => {
    if (!eventId) return false;
    return jobs.some((j) => j.eventId === eventId);
  }, [jobs, eventId]);

  const nextStep = useMemo(
    () => activeSteps.find((s) => !s.isCompleted) ?? null,
    [activeSteps],
  );

  // Preserve track order while grouping so section headers appear once per track.
  const trackGroups = useMemo(() => {
    const groups: { trackKey: string; steps: ActiveStep[] }[] = [];
    for (const step of activeSteps) {
      const last = groups[groups.length - 1];
      if (last && last.trackKey === step.trackKey) {
        last.steps.push(step);
      } else {
        groups.push({ trackKey: step.trackKey, steps: [step] });
      }
    }
    return groups;
  }, [activeSteps]);

  // Clicking a step takes the user straight to the right page and (when the
  // step has a tagged control) arms the anchored pointer card there — no
  // intermediate explainer modal. Mirrors the arm the old StepDetailOverlay
  // CTA did. Steps whose target needs an event but we're not inside one (e.g.
  // the company dashboard) can't resolve — surface a quiet hint instead.
  const handleStepClick = useCallback(
    (step: ActiveStep) => {
      const needsEventId = step.navigationTarget.includes("{eventId}");
      if (needsEventId && !eventId) {
        showToast(t("needsEvent"), "info");
        return;
      }
      // The checklist is company-wide but this action opens the CURRENT event's
      // page. If that event doesn't have the feature the step needs, navigating
      // would dump the user on the locked-door SNoAccess page (e.g. "send first
      // email" on an event without Messaging). Read the shell's cached event
      // (POnboarding lives outside EventProvider) and, when we KNOW the plugin
      // is missing, explain instead of dead-ending. Fail open on unknown.
      if (needsEventId && eventId && step.requiredPlugins?.length) {
        const cachedEvent = queryClient.getQueryData<VeltricEvent | undefined>(
          eventLayoutEventKey(eventId),
        );
        const plugins = cachedEvent?.plugins;
        if (
          plugins &&
          !step.requiredPlugins.every((p) => plugins.includes(p as Plugin))
        ) {
          showToast(t("stepUnavailableHere"), "info");
          return;
        }
      }
      navigate(step.navigationTarget.replace("{eventId}", eventId ?? ""));
      if (step.spotlight) {
        startPointer(step.key, [
          { selector: step.spotlight, textKey: `steps.${step.key}.pointer` },
        ]);
      }
    },
    [eventId, navigate, showToast, t, queryClient],
  );

  // Suppress the onboarding popover under e2e mode. The fullscreen-ish
  // pill at the bottom-right intercepts pointer events on the export
  // confirm/format buttons in the root dashboard, and an empty seed
  // database means it never auto-completes during the test run.
  const isE2eMode = import.meta.env.MODE === "e2e";

  // Always render <AnimatePresence>; conditionally render the inner panel so the
  // exit animation actually plays when isDismissed flips true (after the 5s
  // celebration auto-dismiss). Returning null at the top would short-circuit
  // AnimatePresence and skip the fade-out.
  // Company onboarding contributes content only while it has steps and hasn't
  // been dismissed; the event setup group contributes independently. The panel
  // shows if EITHER has something to show — so a returning user who dismissed
  // the company checklist still sees the setup journey on a fresh event.
  const companyContent = totalCount > 0 && !isDismissed;
  const shouldShowPanel =
    mounted &&
    !journeyActive &&
    !overlayOpen &&
    !crmSuppressed &&
    (companyContent || hasSetup) &&
    !isAuthRoute &&
    !isImmersiveRoute &&
    !isE2eMode &&
    // An explicit minimize wins over everything (PB-FB-N15072): neither a fresh
    // event's auto-open nor the 100 % celebration — which a focus refetch can
    // trigger with no user action at all — re-expands it. Minimized at 100 %,
    // the pill shows the green ring until the provider's auto-dismiss; a fresh
    // event's intro waits for the next manual expand.
    !isMinimized &&
    // Route default (programme canvas) — the celebration and the fresh-event
    // auto-open still surface; everything else stays tucked into the pill.
    (!routeMinimized || justCompletedAll || forceSetupOpen);

  return createPortal(
    <>
    <AnimatePresence>
      {shouldShowPanel && (
      <motion.div
        key="onboarding-panel"
        // Rail offset + transition: keep in lockstep with PBackgroundJobs (the
        // panel this pill stacks above via bottom-20) — same right edge, same
        // 250ms tween as the layout shells, or the two drift apart while the
        // AI drawer opens.
        className={`fixed ${hasVisibleBackgroundJobs ? "bottom-20" : "bottom-4"} right-4 sm:right-[calc(1rem+var(--ai-rail-push))] transition-[right] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none z-300`}
        initial={{ opacity: 0, y: 10, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.95 }}
        transition={{ type: "spring", damping: 25, stiffness: 350 }}
      >
        <div className="surface-card border outline-secondary rounded-xl shadow-lg w-[90vw] sm:w-88 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between pl-4 pr-2.5 pt-3 pb-1">
            <span className="body-2-medium font-primary">{t("title")}</span>
            {!justCompletedAll && (
              <button
                type="button"
                aria-label={t("minimize")}
                onClick={() => {
                  // Drop the auto-open override so an explicit minimize wins
                  // (otherwise forceSetupOpen would keep re-expanding it).
                  setForceSetupOpenId(null);
                  // Also drop a programme-route expand so the route default
                  // (minimized) re-applies next time.
                  setProgrammeExpanded(false);
                  toggleMinimized();
                }}
                className="p-1.5 rounded-md hover:surface-tertiary transition-colors cursor-pointer"
              >
                <IconChevronDown size={15} className="font-tertiary" />
              </button>
            )}
          </div>

          <AnimatePresence mode="wait" initial={false}>
            {justCompletedAll ? (
              <motion.div
                key="celebration"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                className="px-5 pt-2 pb-5 flex flex-col items-center text-center gap-1.5"
              >
                <motion.div
                  initial={{ scale: 0.4, rotate: -15, opacity: 0 }}
                  animate={{ scale: [0.4, 1.15, 1], rotate: [-15, 0, 0], opacity: 1 }}
                  transition={{ duration: 0.55, ease: [0.34, 1.56, 0.64, 1] }}
                >
                  <IconCircleCheckFilled size={40} className="font-success" />
                </motion.div>
                <span className="body-2-medium font-primary mt-1">
                  {t("allDoneTitle")}
                </span>
                <span className="body-4-regular font-secondary">
                  {t("allDoneSubtitle", { completed: completedCount, total: totalCount })}
                </span>
              </motion.div>
            ) : (
              <motion.div
                key="list"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {/* Event-specific setup journey — its own group, above the
                    company checklist. Only inside an event with a live plan. */}
                {hasSetup && setupPlan && eventId && (
                  <OnboardingSetupTrack
                    eventId={eventId}
                    plan={setupPlan}
                    showIntro={showSetupIntro}
                    onDismissIntro={() => setIntroEventId(null)}
                  />
                )}

                {companyContent && (
                <div className={hasSetup ? "border-t outline-secondary" : ""}>
                {/* When the event setup group is present, tuck the company
                    checklist behind a collapsed disclosure (default folded) so
                    the panel isn't cluttered with company-wide "default" steps. */}
                {hasSetup && (
                  <button
                    type="button"
                    onClick={() => setCompanyOpen((v) => !v)}
                    className="w-full flex items-center gap-2 px-4 py-2.5 hover:surface-tertiary transition-colors cursor-pointer"
                  >
                    <span className="flex-1 text-left body-4-medium font-tertiary uppercase tracking-widest">
                      {t("generalSteps")}
                    </span>
                    <span className="body-4-medium font-tertiary tabular-nums">
                      {completedCount}/{totalCount}
                    </span>
                    <IconChevronDown
                      size={14}
                      className={`font-tertiary transition-transform ${companyOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                )}
                {(!hasSetup || companyOpen) && (
                <>
                {/* Progress */}
                <div className="px-4 pb-3">
                  <div className="flex items-baseline justify-between mb-1.5">
                    <span className="body-4-regular font-secondary">
                      {t("progress", { completed: completedCount, total: totalCount })}
                    </span>
                    <span className="body-4-medium font-secondary tabular-nums">
                      {percentComplete}%
                    </span>
                  </div>
                  <div className="w-full h-1 surface-quaternary rounded-full overflow-hidden">
                    <motion.div
                      className="h-full rounded-full cta-primary"
                      initial={{ width: 0 }}
                      animate={{ width: `${percentComplete}%` }}
                      transition={{ duration: 0.4, ease: "easeOut" }}
                    />
                  </div>
                </div>

                {/* Next step */}
                {nextStep && (
                  <div className="px-3 pb-3">
                    <button
                      type="button"
                      onClick={() => handleStepClick(nextStep)}
                      className="w-full text-left rounded-xl border outline-secondary p-3 hover:surface-tertiary transition-colors cursor-pointer"
                    >
                      <span className="body-4-medium font-tertiary uppercase tracking-widest block mb-1">
                        {t("nextStep")}
                      </span>
                      <span className="body-3-medium font-primary block">
                        {t(nextStep.titleKey)}
                      </span>
                      <span className="body-4-regular font-secondary block mt-0.5 line-clamp-2">
                        {t(nextStep.descriptionKey)}
                      </span>
                      <span className="inline-flex items-center gap-1 body-3-medium font-primary mt-2">
                        {t("continue")}
                        <IconArrowRight size={14} />
                      </span>
                    </button>
                  </div>
                )}

                {/* Steps grouped by track */}
                <div className="border-t outline-secondary px-1.5 py-1.5 max-h-72 overflow-y-auto custom-scrollbar">
                  <AnimatePresence mode="popLayout">
                    {trackGroups.map((group) => (
                      <div key={group.trackKey} className="mb-1 last:mb-0">
                        {trackGroups.length > 1 && (
                          <div className="px-2.5 pt-2 pb-1">
                            <span className="body-4-medium font-tertiary uppercase tracking-widest">
                              {t(`tracks.${group.trackKey}.title`)}
                            </span>
                          </div>
                        )}
                        {group.steps.map((step) => (
                          <OnboardingStepRow
                            key={step.key}
                            step={step}
                            justCompleted={recentlyCompletedKeys.has(step.key)}
                            onOpen={() => handleStepClick(step)}
                          />
                        ))}
                      </div>
                    ))}
                  </AnimatePresence>
                </div>
                </>
                )}
                </div>
                )}

                {/* AI assistant entry point — works everywhere: event pages
                    mount the event-scoped provider, the dashboard the
                    company-scoped one. HANDSHAKE: the provider's listener
                    preventDefault()s the (cancelable) event only when the
                    drawer can actually open — we minimize the panel only
                    then, so a click while the assistant is unavailable
                    (role gate, scope still resolving) doesn't collapse the
                    checklist for nothing. */}
                <div className="border-t outline-secondary p-1.5">
                  <button
                    type="button"
                    onClick={() => {
                      const openEvent = new Event("openAiAssistant", { cancelable: true });
                      window.dispatchEvent(openEvent);
                      if (openEvent.defaultPrevented) toggleMinimized();
                    }}
                    className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg hover:surface-tertiary transition-colors cursor-pointer"
                  >
                    <IconSparkles size={14} className="font-blue shrink-0" />
                    <span className="body-3-medium font-primary">{tAi("open")}</span>
                    {/* Hardcoded like SearchButton's ⌘K chip — not a locale string. */}
                    <kbd className="ml-auto flex items-center gap-0.5 rounded-md border px-1.5 py-0.5 body-4-regular font-tertiary outline-quaternary">
                      <span>⌘</span>
                      <span>J</span>
                    </kbd>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
      )}
    </AnimatePresence>
    </>,
    document.body,
  );
};
